import React from "react";
import { motion } from "framer-motion";
import { Database, Filter, Cpu, Gauge, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { AutoLinkPartners } from "@/components/marketing/PartnerLink";

const STEPS = [
  { icon: Database, label: "Data Ingestion", desc: "Bank feeds via Finicity" },
  { icon: Filter, label: "Normalization", desc: "Clean, structure, deduplicate" },
  { icon: Cpu, label: "AI Feature Extraction", desc: "Signals & pattern detection" },
  { icon: Gauge, label: "Scoring Model", desc: "Uptiq AI + ZenMe layer" },
  { icon: CheckCircle2, label: "ZenScore Output", desc: "Readiness + insights" },
];

/**
 * MethodologyDiagram — 5-box horizontal diagram with emerald connector arrows.
 * Stacks vertically on mobile.
 */
export const MethodologyDiagram = ({ className }) => {
  return (
    <div
      className={cn("relative w-full", className)}
      data-testid="methodology-diagram"
    >
      {/* Desktop: horizontal */}
      <ol className="hidden lg:grid lg:grid-cols-9 lg:items-center lg:gap-0">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <React.Fragment key={s.label}>
              <motion.li
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: 0.1 * i, duration: 0.5 }}
                className="col-span-1 flex flex-col items-center gap-2 text-center"
              >
                <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-brand-emerald/25 bg-bg-raised">
                  <Icon className="h-6 w-6 text-brand-emerald" strokeWidth={1.75} />
                  <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-emerald text-[10px] font-semibold text-black">
                    {i + 1}
                  </span>
                </div>
                <span className="mt-2 text-xs font-semibold text-white">
                  {s.label}
                </span>
                <span className="text-[10px] text-text-muted"><AutoLinkPartners text={s.desc} /></span>
              </motion.li>
              {i < STEPS.length - 1 && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: 0.1 * i + 0.15, duration: 0.5 }}
                  className="col-span-1 relative -translate-y-6 origin-left"
                >
                  <svg width="100%" height="24" viewBox="0 0 100 24">
                    <defs>
                      <linearGradient id={`grad-${i}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#00E676" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#00E676" stopOpacity="0.8" />
                      </linearGradient>
                    </defs>
                    <line
                      x1="0"
                      y1="12"
                      x2="88"
                      y2="12"
                      stroke={`url(#grad-${i})`}
                      strokeWidth="1.5"
                    />
                    <polygon points="88,6 100,12 88,18" fill="#00E676" />
                  </svg>
                </motion.div>
              )}
            </React.Fragment>
          );
        })}
      </ol>

      {/* Mobile: vertical */}
      <ol className="flex flex-col gap-4 lg:hidden">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.li
              key={s.label}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: 0.06 * i, duration: 0.45 }}
              className="flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4"
            >
              <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-brand-emerald/25 bg-bg-raised">
                <Icon className="h-5 w-5 text-brand-emerald" strokeWidth={1.75} />
                <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-emerald text-[10px] font-semibold text-black">
                  {i + 1}
                </span>
              </div>
              <div>
                <div className="text-sm font-semibold text-white">
                  {s.label}
                </div>
                <div className="text-xs text-text-muted"><AutoLinkPartners text={s.desc} /></div>
              </div>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
};

export default MethodologyDiagram;
